import { useState, useCallback } from 'react';
import { useApiQuery } from './useApiQuery';
import type { ScanListItem, ScansResponse } from '../types/scans';

interface UseScansOptions {
  page?: number;
  limit?: number;
  search?: string;
}

interface UseScansResult {
  scans: ScanListItem[];
  isLoading: boolean;
  hasData: boolean;
  error: Error | null;
  total: number;
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  setLimit: (limit: number) => void;
  refetch: () => void;
}

const EMPTY_ITEMS: ScanListItem[] = [];

export function useScans(options: UseScansOptions = {}): UseScansResult {
  const {
    page: initialPage = 1,
    limit: initialLimit = 50,
    search: initialSearch = '',
  } = options;
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState(initialSearch);
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (search.trim()) params.set('search', search.trim());
  const query = useApiQuery<ScansResponse>(`/meta/scans?${params}`);
  const refresh = query.refetch;
  const refetch = useCallback(() => {
    void refresh();
  }, [refresh]);
  const data = query.data;
  return {
    scans: data?.items ?? EMPTY_ITEMS,
    total: data?.total ?? 0,
    hasData: data !== undefined,
    isLoading: query.isLoading,
    error: query.error,
    page,
    limit,
    setPage,
    setSearch,
    setLimit,
    refetch,
  };
}
